import { Icon, IconButton, Spin, tryParseJSONObject, Typography } from '@fox/atoms';
import { IBlockDefinition } from '@fox/blocks';
import { FlexColumnSmallSpaced, FlexRowSmallSpacedCentered, FlexWrapRowSpaced } from '@fox/layout';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { UPLOAD_FILES_BLOCK_TYPE } from '../../../form-block-types';
import { IQuestionAnswer } from '../../form-root/contracts';
import { FormRenderMode, useFormRuntimeContext } from '../../form-runtime-context';
import { useFormBlockReRenderer } from '../../use-form-block-re-renderer';
import { QuestionDescriptionRender } from '../question-block/question-description';
import { QuestionLabelRender } from '../question-block/question-label';
import { UploadedFilesAnswer, UploadedFilesWithMeta, UploadFileBlockMeta, UploadFilesBlockProps } from './contracts';

export const UploadFilesBlock = ({ props, id }: UploadFilesBlockProps) => {
	const { formRenderMode, uploadFile, getAnswer, answerQuestion } = useFormRuntimeContext();
	const [uploading, setUploading] = useState(false);
	const [error, setError] = useState(false);
	useFormBlockReRenderer([props!.name!]);

	const fieldMeta = tryParseJSONObject<UploadFileBlockMeta>(props?.metadataJson, {});

	const allowDrop = formRenderMode !== FormRenderMode.design;

	const currentAnswer: IQuestionAnswer = getAnswer(props!.name!) ?? {
		questionName: props?.name ?? 'UploadFiles'
	};
	if (!currentAnswer.answer) {
		currentAnswer.answer = {};
	}

	// const onDrop = useCallback(
	const onDrop = async (acceptedFiles: File[]) => {
		setUploading(true);
		setError(false);
		try {
			for (const currentFile of acceptedFiles) {
				const answer = tryParseJSONObject<UploadedFilesAnswer>(currentAnswer.answer?.answerJson, {});
				const file = await uploadFile({ file: currentFile });
				if (file) {
					answer.uploadedFiles = [...(answer.uploadedFiles ?? []), file.url];
					answer.uploadedFilesWithMeta = [...(answer.uploadedFilesWithMeta ?? []), file];
				}

				currentAnswer.answer!.answerJson = JSON.stringify(answer);
				answerQuestion(currentAnswer);
			}
		} catch (e) {
			console.error('Error occurred while uploading a file:', e);
			setError(true);
		} finally {
			setUploading(false);
		}
	};
	// 	[JSON.stringify(answers)]
	// );

	const deleteFile = (file: UploadedFilesWithMeta) => {
		const answer = tryParseJSONObject<UploadedFilesAnswer>(currentAnswer.answer?.answerJson, {});
		answer.uploadedFiles = (answer.uploadedFiles ?? []).filter(f => f !== file.url);
		answer.uploadedFilesWithMeta = (answer.uploadedFilesWithMeta ?? []).filter(f => f.url !== file.url);
		currentAnswer.answer!.answerJson = JSON.stringify(answer);
		answerQuestion(currentAnswer);
	};

	const { getRootProps, getInputProps } = useDropzone({ onDrop });
	const files = tryParseJSONObject<UploadedFilesAnswer>(currentAnswer?.answer?.answerJson, {}).uploadedFilesWithMeta ?? [];

	return (
		<FlexColumnSmallSpaced style={{ marginBottom: 20 }}>
			<QuestionLabelRender value={props?.title || 'Upload'} id={id} required={fieldMeta.required} />
			<Spin spinning={uploading} tip="Uploading">
				<div {...(allowDrop ? getRootProps() : {})}>
					{allowDrop ? <input {...getInputProps()} /> : null}

					<div style={{ width: '100%' }}>
						<div style={{ display: 'flex', justifyContent: 'space-around', cursor: 'pointer', marginBottom: 15 }}>
							<div>
								<Icon iconName="Upload" fontSize="large" />
							</div>
						</div>

						<div style={{ width: '100%', display: 'flex', justifyContent: 'space-around' }}>
							<QuestionDescriptionRender value={props?.description ?? ''} id={id} />
						</div>
					</div>
				</div>
			</Spin>
			{error ? <Typography>Sorry, we were unable to upload your file. Please check the file and try again.</Typography> : null}
			<FlexWrapRowSpaced>
				{files?.map((a, i) => {
					return (
						<FlexRowSmallSpacedCentered key={a.url}>
							<Typography variant="body1">{a.fileName}</Typography>
							<IconButton
								iconName="Clear"
								size="small"
								iconColor="error"
								onClick={() => {
									deleteFile(a);
								}}
							/>
						</FlexRowSmallSpacedCentered>
					);
				})}
			</FlexWrapRowSpaced>
		</FlexColumnSmallSpaced>
	);
};

export const UploadFilesBlockRuntime: IBlockDefinition = {
	Component: UploadFilesBlock,
	type: UPLOAD_FILES_BLOCK_TYPE
};
